// src/components/Booklet/BookletViewer.js (Fixed Navigation)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn,
  ZoomOut,
  Maximize,
  Download,
  Share,
  X,
  Play,
  Pause,
  AlertCircle,
  ArrowLeft,
  Menu,
  BookOpen,
  Loader2
} from 'lucide-react';
import { bookletService } from '../../services/bookletService';
import PageTransitions from './PageTransitions';
import toast from 'react-hot-toast';

const BookletViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booklet, setBooklet] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showPopup, setShowPopup] = useState(null);
  const [audioElements, setAudioElements] = useState({});
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const viewerRef = useRef(null);
  const autoPlayRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const navigationTimeoutRef = useRef(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-hide controls on mobile
  useEffect(() => {
    if (isMobile) {
      const resetControlsTimeout = () => {
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      };

      setShowControls(true);
      resetControlsTimeout();

      return () => {
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
      };
    }
  }, [currentPageIndex, isMobile]);

  useEffect(() => {
    if (id) {
      loadBooklet();
    } else {
      setError('No booklet ID provided');
      setIsLoading(false);
    }
  }, [id]);

  const loadBooklet = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const bookletData = await bookletService.getPublicBooklet(id);
      
      if (!bookletData.pages || !Array.isArray(bookletData.pages)) {
        throw new Error('Invalid booklet data: pages not found');
      }

      if (bookletData.pages.length === 0) {
        throw new Error('Booklet has no pages');
      }

      // Validate and sanitize page data
      const sanitizedPages = bookletData.pages.map((page, index) => {
        const sanitizedPage = {
          ...page,
          pageNumber: page.pageNumber || index + 1,
          url: page.url || page.imageUrl || '',
          thumbnailUrl: page.thumbnailUrl || page.url || page.imageUrl || '',
          dimensions: {
            width: Number.isFinite(page.dimensions?.width) ? page.dimensions.width : 600,
            height: Number.isFinite(page.dimensions?.height) ? page.dimensions.height : 800
          },
          interactions: Array.isArray(page.interactions) ? page.interactions.map(interaction => ({
            ...interaction,
            x: Number.isFinite(interaction.x) ? interaction.x : 0,
            y: Number.isFinite(interaction.y) ? interaction.y : 0,
            width: Number.isFinite(interaction.width) ? interaction.width : 10,
            height: Number.isFinite(interaction.height) ? interaction.height : 10
          })) : []
        };
        return sanitizedPage;
      });

      setBooklet(bookletData);
      setPages(sanitizedPages);

      if (currentPageIndex >= sanitizedPages.length) {
        setCurrentPageIndex(0);
      }

    } catch (error) {
      console.error('Error loading booklet:', error);
      setError(error.message);
      
      if (error.message.includes('not found')) {
        setError('This booklet could not be found. It may have been deleted or the link is incorrect.');
      } else if (error.message.includes('Access denied')) {
        setError('This booklet is private and cannot be viewed.');
      } else if (error.message.includes('Invalid booklet data')) {
        setError('This booklet appears to be corrupted or incomplete.');
      } else {
        setError('Unable to load booklet. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Simplified page navigation without disabled state
  const navigateToPage = useCallback((newIndex) => {
    console.log('Navigating to page:', newIndex, 'from:', currentPageIndex);
    
    if (newIndex >= 0 && newIndex < pages.length && newIndex !== currentPageIndex) {
      setCurrentPageIndex(newIndex);
      
      if (isMobile) {
        setShowControls(true);
      }
      
      // Clear any existing navigation timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    }
  }, [currentPageIndex, pages.length, isMobile]);

  const nextPage = useCallback(() => {
    console.log('Next page clicked, current:', currentPageIndex, 'max:', pages.length - 1);
    if (currentPageIndex < pages.length - 1) {
      navigateToPage(currentPageIndex + 1);
    }
  }, [currentPageIndex, pages.length, navigateToPage]);

  const prevPage = useCallback(() => {
    console.log('Previous page clicked, current:', currentPageIndex);
    if (currentPageIndex > 0) {
      navigateToPage(currentPageIndex - 1);
    }
  }, [currentPageIndex, navigateToPage]);

  const goToPage = useCallback((index) => {
    console.log('Go to page:', index);
    navigateToPage(index);
  }, [navigateToPage]);

  // Safe zoom functions
  const handleZoomIn = () => {
    const newZoom = Math.min(3, zoom + 0.25);
    if (Number.isFinite(newZoom)) {
      setZoom(newZoom);
    }
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(0.5, zoom - 0.25);
    if (Number.isFinite(newZoom)) {
      setZoom(newZoom);
    }
  };

  const resetZoom = () => {
    setZoom(1);
  };

  // Keyboard navigation - removed isTransitioning check
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showPopup) return;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          console.log('Keyboard: Previous page');
          prevPage();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          console.log('Keyboard: Next page');
          nextPage();
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
        case 'f':
        case 'F11':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'Escape':
          if (isFullscreen) {
            toggleFullscreen();
          } else if (showPopup) {
            setShowPopup(null);
          }
          break;
        case ' ':
          e.preventDefault();
          if (isAutoPlaying) {
            stopAutoPlay();
          } else {
            startAutoPlay();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPageIndex, pages.length, isFullscreen, showPopup, isAutoPlaying, prevPage, nextPage]);

  // Touch navigation - removed isTransitioning check
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    console.log('Touch swipe:', { distance, isLeftSwipe, isRightSwipe });

    if (isLeftSwipe && currentPageIndex < pages.length - 1) {
      nextPage();
    }
    if (isRightSwipe && currentPageIndex > 0) {
      prevPage();
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (viewerRef.current.requestFullscreen) {
        viewerRef.current.requestFullscreen();
      } else if (viewerRef.current.webkitRequestFullscreen) {
        viewerRef.current.webkitRequestFullscreen();
      } else if (viewerRef.current.msRequestFullscreen) {
        viewerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleInteractionClick = (interaction) => {
    switch (interaction.type) {
      case 'link':
        if (interaction.data?.url) {
          if (interaction.data.openInNewTab) {
            window.open(interaction.data.url, '_blank');
          } else {
            window.location.href = interaction.data.url;
          }
        }
        break;

      case 'video':
        openVideoModal(interaction.data);
        break;

      case 'audio':
        playAudio(interaction.id, interaction.data);
        break;

      case 'popup':
        setShowPopup(interaction.data);
        break;
    }
  };

  const openVideoModal = (videoData) => {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="relative max-w-4xl w-full mx-4">
        <button class="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        <div class="bg-black rounded-lg overflow-hidden">
          ${getVideoEmbed(videoData.videoUrl, videoData.autoplay, videoData.controls)}
        </div>
      </div>
    `;

    modal.querySelector('button').addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });

    document.body.appendChild(modal);
  };

  const getVideoEmbed = (url, autoplay = false, controls = true) => {
    if (!url) return '<p>No video URL provided</p>';
    
    const autoplayParam = autoplay ? '&autoplay=1' : '';
    const controlsParam = controls ? '&controls=1' : '&controls=0';

    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = extractYouTubeId(url);
      if (!videoId) return '<p>Invalid YouTube URL</p>';
      return `<iframe width="100%" height="400" src="https://www.youtube.com/embed/${videoId}?${autoplayParam}${controlsParam}" frameborder="0" allowfullscreen></iframe>`;
    } else if (url.includes('vimeo.com')) {
      const videoId = extractVimeoId(url);
      if (!videoId) return '<p>Invalid Vimeo URL</p>';
      return `<iframe width="100%" height="400" src="https://player.vimeo.com/video/${videoId}?${autoplayParam}" frameborder="0" allowfullscreen></iframe>`;
    } else {
      return `<video width="100%" height="400" ${controls ? 'controls' : ''} ${autoplay ? 'autoplay' : ''}><source src="${url}" type="video/mp4">Your browser does not support the video tag.</video>`;
    }
  };

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const extractVimeoId = (url) => {
    const regExp = /vimeo.com\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const playAudio = (interactionId, audioData) => {
    if (!audioData?.audioUrl) {
      toast.error('No audio URL provided');
      return;
    }

    Object.values(audioElements).forEach(audio => {
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    let audio = audioElements[interactionId];
    if (!audio) {
      audio = new Audio(audioData.audioUrl);
      audio.loop = audioData.loop;
      setAudioElements(prev => ({ ...prev, [interactionId]: audio }));
    }

    if (audio.paused) {
      audio.play().catch(console.error);
    } else {
      audio.pause();
    }
  };

  const shareBooklet = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: booklet?.title || 'Booklet',
          text: booklet?.description || '',
          url: url
        });
      } catch (error) {
        navigator.clipboard.writeText(url);
        toast.success('Booklet link copied to clipboard');
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Booklet link copied to clipboard');
    }
  };

  const downloadBooklet = () => {
    if (booklet?.downloadUrl) {
      const link = document.createElement('a');
      link.href = booklet.downloadUrl;
      link.download = `${booklet.title}.pdf`;
      link.click();
    } else {
      toast.error('Download not available');
    }
  };

  const startAutoPlay = () => {
    setIsAutoPlaying(true);
    autoPlayRef.current = setInterval(() => {
      setCurrentPageIndex(prev => {
        if (prev >= pages.length - 1) {
          setIsAutoPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 3000);
  };

  const stopAutoPlay = () => {
    setIsAutoPlaying(false);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  };

  const goToBooklets = () => {
    navigate('/booklets');
  };

  useEffect(() => {
    if (booklet && !error) {
      bookletService.trackBookletView(id).catch(console.error);
    }
  }, [booklet, id, error]);

  useEffect(() => {
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      Object.values(audioElements).forEach(audio => {
        audio.pause();
      });
    };
  }, [audioElements]);

  // Debug current page changes
  useEffect(() => {
    console.log('Current page index changed to:', currentPageIndex);
  }, [currentPageIndex]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white mx-auto animate-spin" />
          <p className="mt-4 text-white">Loading booklet...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Unable to Load Booklet</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={loadBooklet}
              className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={goToBooklets}
              className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Back to Booklets
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!booklet || !pages.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center text-white">
          <p>No booklet data available</p>
          <button
            onClick={goToBooklets}
            className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center mx-auto"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Back to Booklets
          </button>
        </div>
      </div>
    );
  }

  const currentPage = pages[currentPageIndex];
  const pageInteractions = currentPage?.interactions || [];

  return (
    <div 
      ref={viewerRef}
      className={`min-h-screen bg-gray-900 text-white transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}
      onClick={() => isMobile && setShowControls(true)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      {(!isMobile || showControls) && booklet.settings?.showNavigation && (
        <div className={`bg-black bg-opacity-50 backdrop-blur-sm px-4 md:px-6 py-4 transition-all duration-300 ${isMobile && !showControls ? 'opacity-0 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {!isFullscreen && (
                <button
                  onClick={goToBooklets}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Back to Booklets"
                >
                  <BookOpen className="w-5 h-5" />
                </button>
              )}
              <div>
                <h1 className="text-lg md:text-xl font-semibold truncate max-w-xs md:max-w-none">
                  {booklet.title}
                </h1>
                {booklet.settings?.showPageNumbers && (
                  <p className="text-sm text-gray-300">
                    Page {currentPageIndex + 1} of {pages.length}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-1 md:space-x-2">
              {/* Auto-play controls */}
              <button
                onClick={isAutoPlaying ? stopAutoPlay : startAutoPlay}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                title={isAutoPlaying ? "Stop Auto-play" : "Start Auto-play"}
              >
                {isAutoPlaying ? (
                  <Pause className="w-4 h-4 md:w-5 md:h-5" />
                ) : (
                  <Play className="w-4 h-4 md:w-5 md:h-5" />
                )}
              </button>

              {/* Zoom controls */}
              {!isMobile && (
                <>
                  <button
                    onClick={handleZoomOut}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                  <span className="text-xs md:text-sm min-w-8 md:min-w-12 text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </>
              )}

              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                title="Toggle Fullscreen"
              >
                <Maximize className="w-4 h-4 md:w-5 md:h-5" />
              </button>

              {/* Share */}
              <button
                onClick={shareBooklet}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                title="Share Booklet"
              >
                <Share className="w-4 h-4 md:w-5 md:h-5" />
              </button>

              {/* Download (if allowed) */}
              {booklet.settings?.allowDownload && (
                <button
                  onClick={downloadBooklet}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Download PDF"
                >
                  <Download className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              )}

              {/* Mobile menu toggle */}
              {isMobile && (
                <button
                  onClick={() => setShowThumbnails(!showThumbnails)}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Toggle Pages"
                >
                  <Menu className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-2 md:px-4 py-2 md:py-4">
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Navigation Arrows - REMOVED disabled prop */}
          {currentPageIndex > 0 && (!isMobile || showControls) && (
            <button
              onClick={prevPage}
              className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 md:p-3 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full transition-all duration-200 backdrop-blur-sm"
              title="Previous page"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          )}

          {currentPageIndex < pages.length - 1 && (!isMobile || showControls) && (
            <button
              onClick={nextPage}
              className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 md:p-3 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full transition-all duration-200 backdrop-blur-sm"
              title="Next page"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          )}

          {/* Page Content */}
          {currentPage && (
            <PageTransitions
              transition={booklet.settings?.pageTransition || 'fade'}
              currentPage={currentPage}
              zoom={zoom}
              interactions={pageInteractions}
              onInteractionClick={handleInteractionClick}
            />
          )}
        </div>
      </div>

      {/* Page Thumbnails (bottom) */}
      {(!isMobile || showThumbnails) && booklet.settings?.showNavigation && pages.length > 1 && (
        <div className={`bg-black bg-opacity-50 backdrop-blur-sm p-2 md:p-4 transition-all duration-300 ${isMobile && !showControls ? 'opacity-0 pointer-events-none' : ''}`}>
          <div className="flex justify-center space-x-1 md:space-x-2 overflow-x-auto pb-2">
            {pages.map((page, index) => (
              <button
                key={index}
                onClick={() => goToPage(index)}
                className={`
                  relative flex-shrink-0 w-12 h-16 md:w-16 md:h-20 border-2 rounded overflow-hidden transition-all duration-200
                  ${currentPageIndex === index
                    ? 'border-white shadow-lg'
                    : 'border-gray-500 hover:border-gray-300'
                  }
                `}
                title={`Go to page ${index + 1}`}
              >
                <img
                  src={page.thumbnailUrl}
                  alt={`Page ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = page.url; // Fallback to main URL
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs text-center py-0.5">
                  {index + 1}
                </div>
                {page.interactions?.length > 0 && (
                  <div className="absolute top-1 right-1 w-2 h-2 bg-indigo-500 rounded-full"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white text-black rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold">{showPopup.title}</h3>
              <button
                onClick={() => setShowPopup(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="prose max-w-none">
                {showPopup.content?.split('\n').map((line, index) => (
                  <p key={index} className="mb-2">{line}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug info (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 left-4 bg-black bg-opacity-75 text-white p-2 rounded text-xs">
          Page: {currentPageIndex + 1}/{pages.length}
        </div>
      )}

      {/* Loading indicator for auto-play */}
      {isAutoPlaying && (
        <div className="fixed bottom-4 left-4 bg-black bg-opacity-75 text-white px-3 py-2 rounded-lg text-sm">
          Auto-playing...
        </div>
      )}

      {/* Help overlay for first-time users */}
      {!isMobile && currentPageIndex === 0 && !showPopup && (
        <div className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white p-4 rounded-lg text-sm max-w-xs">
          <p className="mb-2 font-medium">Navigation:</p>
          <ul className="text-xs space-y-1">
            <li>← → : Navigate pages</li>
            <li>+ - : Zoom in/out</li>
            <li>Space : Auto-play</li>
            <li>F : Fullscreen</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default BookletViewer;